import { type Party } from '@prisma/client'
import { useState } from 'react'
import { Avatar, AvatarImage, AvatarFallback } from '#app/components/ui/avatar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '#app/components/ui/card.js'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '#app/components/ui/hover-card.js'
import { type SpeechWithMember } from '#app/models.server/speeches/index.js'
import { SpeechSummary } from './SpeechSummary'

export function TailwindDummy() {
  return (
    <>
      <div className="border-blue-500"></div>
      <div className="border-red-500"></div>
      <div className="border-yellow-500"></div>
    </>
  )
}

export default function Speakers({
  speeches,
}: {
  speeches: SpeechWithMember[]
}) {
  const speechesBySpeaker = speeches.reduce(
    (acc, speech) => {
      const speaker = speech.member
      if (!speaker) {
        return acc
      }
      if (!acc[speaker.id]) {
        acc[speaker.id] = {
          member: speaker,
          speeches: [],
        }
      }
      acc[speaker.id].speeches.push(speech)
      return acc
    },
    {} as Record<
      number,
      {
        member: NonNullable<SpeechWithMember['member']>
        speeches: SpeechWithMember[]
      }
    >,
  )

  const [selectedSpeaker, setSelectedSpeaker] = useState<number | null>(null)

  const partyColor = (party: Party) =>
    ({
      Con: 'blue-500',
      Lab: 'red-500',
      LD: 'yellow-500',
    })[party.abbreviation || 'unknown'] || 'gray-500'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Speakers</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="mb-6 flex flex-wrap gap-3">
            {Object.values(speechesBySpeaker).map(speaker => (
              <div
                className="cursor-pointer"
                key={speaker.member.id}
                onClick={() => setSelectedSpeaker(speaker.member.id)}
              >
                <HoverCard>
                  <HoverCardTrigger>
                    <Avatar
                      className={`h-12 w-12 border-4 border-${partyColor(speaker.member.latestParty)}`}
                    >
                      <AvatarImage
                        alt={speaker.member.nameDisplayAs}
                        src={speaker.member.thumbnailUrl}
                      />
                      <AvatarFallback>
                        {speaker.member.nameDisplayAs}
                      </AvatarFallback>
                    </Avatar>
                  </HoverCardTrigger>
                  <HoverCardContent>
                    <div className="font-semibold">
                      {speaker.member.nameDisplayAs}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {speaker.member.latestParty.name}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
            ))}
          </div>
          {selectedSpeaker && speechesBySpeaker[selectedSpeaker] && (
            <div>
              <h2 className="mb-2 text-lg font-bold">
                {speechesBySpeaker[selectedSpeaker].member.nameDisplayAs}
              </h2>
              {speechesBySpeaker[selectedSpeaker].speeches
                .slice(0, 1)
                .map(speech => (
                  <div key={speech.id}>
                    <SpeechSummary speechId={speech.id} />
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
